import { EMPTY, fromEvent, merge, Observable, of } from 'rxjs'
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
} from 'rxjs/operators'
import { PageActivity } from 'types/definitions'

const ifvisible = require('ifvisible.js')

/**
 * Determines if an element is visible in the viewport.
 *
 * @param element
 * @param percentX
 * @param percentY
 * @returns {boolean}
 */
export function inView(element: HTMLElement, percentX = 100, percentY = 100) {
  const tolerance = 0.01 // needed because the rects returned by getBoundingClientRect provide the position up to 10 decimals
  const elementRect = element.getBoundingClientRect()
  const parentRects = []

  while (element.parentElement !== null) {
    parentRects.push(element.parentElement.getBoundingClientRect())
    element = element.parentElement
  }

  return parentRects.every((parentRect) => {
    const visiblePixelX =
      Math.min(elementRect.right, parentRect.right) -
      Math.max(elementRect.left, parentRect.left)
    const visiblePixelY =
      Math.min(elementRect.bottom, parentRect.bottom) -
      Math.max(elementRect.top, parentRect.top)
    const visiblePercentageX = (visiblePixelX / elementRect.width) * 100
    const visiblePercentageY = (visiblePixelY / elementRect.height) * 100
    percentY =
      elementRect.height > parentRect.height
        ? (parentRect.height / elementRect.height) * 100
        : 100
    return (
      visiblePercentageX + tolerance > percentX &&
      visiblePercentageY + tolerance > percentY
    )
  })
}

/**
 * Gets a stream for page visibility.
 */
export function pageActivityStream(): Observable<PageActivity> {
  return new Observable<PageActivity>((observer) => {
    const onActive = () => observer.next(PageActivity.Active)
    const onInactive = () => observer.next(PageActivity.Inactive)
    const onExit = () => observer.next(PageActivity.Exit)
    ifvisible.on('focus', onActive)
    ifvisible.on('wakeup', onActive)
    ifvisible.on('blur', onInactive)
    ifvisible.on('idle', onInactive)
    window.addEventListener('beforeunload', onExit)
    return () => {
      ifvisible.off('focus', onActive)
      ifvisible.off('wakeup', onActive)
      ifvisible.off('blur', onInactive)
      ifvisible.off('idle', onInactive)
      window.removeEventListener('beforeunload', onExit)
    }
  }).pipe(distinctUntilChanged())
}

/**
 * Scroll direction.
 */
export enum ScrollDirection {
  /**
   * Not scrolling.
   */
  Idle = 'Idle',
  /**
   * Scrolling up.
   */
  Up = 'Up',
  /**
   * Scrolling down.
   */
  Down = 'Down',
  /**
   * Scrolling left.
   */
  Left = 'Left',
  /**
   * Scrolling right.
   */
  Right = 'Right',
}

/**
 * Scroll position.
 */
export interface ScrollPosition {
  /**
   * The total height of the scroll container (**not** just the viewable area)
   */
  scrollHeight: number
  /**
   * The position of the scrollbar top edge.
   */
  scrollTop: number
  /**
   * The position of the scrollbar bottom edge.
   */
  scrollBottom: number
  /**
   * Same as `scrollHeight`, but horizontally.
   */
  scrollWidth: number
  /**
   * The poosition of the scrollbar's left edge.
   */
  scrollLeft: number
  /**
   * The poosition of the scrollbar's right edge.
   */
  scrollRight: number
  /**
   * Whether the element is currently scrollable on the X (horizontal) axis.
   */
  isScrollableX: boolean
  /**
   * Whether the element is currently scrollable on the Y (vertical) axis.
   */
  isScrollableY: boolean
}

/**
 * A pair of scroll positions. The first entry is the previous
 * scroll position, the second one is the current.
 */
export type ScrollPositionPair = [ScrollPosition, ScrollPosition]

/**
 * Scroll activity event.
 */
export interface ScrollActivity extends ScrollPosition {
  /**
   * The direction being scrolled.
   */
  direction: ScrollDirection
  /**
   * The normalized (0 to 1) position of how far we've scrolled on the X axis.
   */
  normalizedX: number
  /**
   * The normalized (0 to 1) position of how far we've scrolled on the Y axis.
   */
  normalizedY: number
}

/**
 * Observes scroll activity for an element.
 *
 * @param element the element to monitor scrolling for.
 * @param emitInitial whether to emit an initial value with the current scroll position
 */
export function scrollActivityStream(
  element: HTMLElement,
  emitInitial = false,
): Observable<ScrollActivity> {
  // Create scroll positions from scroll events.
  const scroll$ = fromEvent(element, 'scroll').pipe(
    map((e) => createScrollPositionFromElement(e.target as HTMLElement)),
  )

  // `pairwise` gives us an observable of pairs where the
  // first entry is the previous emission and the second one is
  // the current emission.
  const pairs$ = scroll$.pipe(pairwise())

  // To figure out what direction we are scrolling,
  // we simply compare the previous scroll with the current scroll.
  // That's what pairwise lets us to.
  const scrollingDown$ = pairs$.pipe(
    filter(([prev, next]) => prev.scrollTop < next.scrollTop),
    mapToDirection(ScrollDirection.Down),
  )
  const scrollingUp$ = pairs$.pipe(
    filter(([prev, next]) => prev.scrollTop > next.scrollTop),
    mapToDirection(ScrollDirection.Up),
  )
  const scrollingLeft$ = pairs$.pipe(
    filter(([prev, next]) => prev.scrollLeft < next.scrollLeft),
    mapToDirection(ScrollDirection.Left),
  )
  const scrollingRight$ = pairs$.pipe(
    filter(([prev, next]) => prev.scrollLeft > next.scrollLeft),
    mapToDirection(ScrollDirection.Right),
  )
  const stopped$ = pairs$.pipe(
    // 200ms without any activity means we stopped.
    debounceTime(200),
    mapToDirection(ScrollDirection.Idle),
  )

  return merge(
    // Whether or not to emit the current scroll position immediately.
    emitInitial
      ? of(createScrollPositionFromElement(element)).pipe(
          // Create a pair consisting of the same value.
          map((v): ScrollPositionPair => [v, v]),
          mapToDirection(ScrollDirection.Idle),
        )
      : EMPTY,
    scrollingDown$,
    scrollingUp$,
    scrollingLeft$,
    scrollingRight$,
    stopped$,
  )
}

/**
 * Creates a ScrollPosition from a target.
 *
 * @param target
 */
function createScrollPositionFromElement(target: HTMLElement): ScrollPosition {
  return {
    scrollHeight: target.scrollHeight,
    scrollWidth: target.scrollWidth,
    scrollTop: target.scrollTop,
    scrollLeft: target.scrollLeft,
    // These are convenience props.
    scrollBottom: target.scrollTop + target.clientHeight,
    scrollRight: target.scrollLeft + target.clientWidth,
    isScrollableX: target.clientWidth < target.scrollWidth,
    isScrollableY: target.clientHeight < target.scrollHeight,
  }
}

/**
 * Maps a pairwise observable of positions to scroll activities with the specified direction.
 *
 * @param direction
 */
function mapToDirection(direction: ScrollDirection) {
  return (
    source$: Observable<ScrollPositionPair>,
  ): Observable<ScrollActivity> =>
    source$.pipe(
      map(([, next]) => ({
        direction,
        ...next,
        ...computePercentages(next),
      })),
    )
}

/**
 * Computes scroll percentages.
 *
 * @param position
 */
function computePercentages(position: ScrollPosition) {
  /*
    Here follows a visual explanation of how percentages are calculated.
    Math is hard.

        |---------------[==========]---------------------|
                        ^          ^                     ^
                  scrollLeft  scrollRight         scrollWidth
                       17         28                     50

        |---------------[==========]---------------------|
                        ^^^^^^^^^^^^
                          sbWidth
                            12

        |---------------[==========]---------------------|
                                             ^
                                           sbLeft
                                            38

        |---------------[==========]---------------------|
                             ^
                       normalizedX
                    17 / 38 = 0.4473684211
                          (~22)
  */
  const sbWidth = position.scrollLeft - position.scrollRight
  const sbLeft = position.scrollWidth - sbWidth
  const normalizedX = position.scrollLeft / sbLeft

  const sbHeight = position.scrollBottom - position.scrollTop
  const sbTop = position.scrollHeight - sbHeight
  const normalizedY = position.scrollTop / sbTop
  return {
    normalizedX,
    normalizedY,
  }
}
